import type { ModalWheelDirection } from './types';

type ModalWheelListener = (wheel_event: WheelEvent) => void;

/**
 * // Class Obj: モーダル内のホイール操作を判定する
 * // Note:
 * // - PC の mouse wheel / trackpad scroll を監視します。
 * // - 上下スクロールのみを group 移動として扱います。
 * // - 連続発火しすぎないように、一定時間内の再実行を抑制します。
 */
export class ModalWheelHandler
{
  private static readonly MIN_DELTA_Y = 40;
  private static readonly INTERVAL_MS = 450;

  private readonly mListenerMap: Map<HTMLElement, ModalWheelListener> = new Map();

  private mLastExecutedTime: number = 0;

  /**
   * // Method: 指定要素へ wheel イベントを登録する
   */
  public bind(
    target_element: HTMLElement,
    callback: (wheel_direction: ModalWheelDirection) => void,
    should_ignore_event: (wheel_event: WheelEvent) => boolean = () => false,
  ): void
  {
    this.destroyByElement(target_element);

    const wheel_listener: ModalWheelListener = (wheel_event: WheelEvent): void =>
    {
      this.handleWheel(wheel_event, callback, should_ignore_event);
    };

    target_element.addEventListener('wheel', wheel_listener, { passive: false });
    this.mListenerMap.set(target_element, wheel_listener);
  }

  /**
   * // Method: 登録済み wheel event をすべて解除する
   */
  public destroy(): void
  {
    Array.from(this.mListenerMap.keys()).forEach((target_element: HTMLElement) =>
    {
      this.destroyByElement(target_element);
    });

    this.mLastExecutedTime = 0;
  }

  /**
   * // Method: 指定要素に登録した wheel event を解除する
   */
  private destroyByElement(target_element: HTMLElement): void
  {
    const wheel_listener: ModalWheelListener | undefined = this.mListenerMap.get(target_element);

    if (!wheel_listener)
    {
      return;
    }

    target_element.removeEventListener('wheel', wheel_listener);
    this.mListenerMap.delete(target_element);
  }

  /**
   * // Method: ホイール方向を判定する
   */
  private handleWheel(
    wheel_event: WheelEvent,
    callback: (wheel_direction: ModalWheelDirection) => void,
    should_ignore_event: (wheel_event: WheelEvent) => boolean,
  ): void
  {
    if (should_ignore_event(wheel_event))
    {
      return;
    }

    const abs_delta_y: number = Math.abs(wheel_event.deltaY);

    if (abs_delta_y < ModalWheelHandler.MIN_DELTA_Y)
    {
      return;
    }

    const current_time: number = Date.now();

    if (current_time - this.mLastExecutedTime < ModalWheelHandler.INTERVAL_MS)
    {
      wheel_event.preventDefault();
      return;
    }

    this.mLastExecutedTime = current_time;

    wheel_event.preventDefault();

    callback(wheel_event.deltaY > 0 ? 'down' : 'up');
  }
}
